import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../services/api';
import { User, ApiResponse } from '../types';
import { PublicBrandingData } from '../services/merchantBrandingApi';
import { clearImpersonationState } from '../services/impersonationApi';

// Merchant-staff context (Wave 4.3.4) — surfaced by /auth/me so the UI can
// gate nav + actions by permission. null for non-staff users.
export interface StaffContext {
  staffOf: string | null;
  permissions: string[];
}

interface AuthState {
  user: User | null;
  parentBranding: PublicBrandingData | null;
  staff: StaffContext | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface LoginCredentials {
  email: string;
  password: string;
}

// BP-68 — `role` was `string`, which let a caller of this thunk ask
// /api/auth/signup for any role it liked, up to and including 'admin'. The
// endpoint now creates customers only; the type says so, so a future caller is
// stopped at compile time rather than by the server's refusal. Merchants sign
// up through the merchant-signup pages, and staff and administrators are never
// signed up for at all — see backend/src/AUTH_README.md.
interface SignupData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: 'customer';
  parentId?: string;
  company?: string;
  phone?: string;
}

interface AuthResponse {
  user: User;
  tokens: {
    accessToken: string;
    refreshToken: string;
  };
}

// Wave 4.3 — when a user with 2FA enrolled submits correct credentials, the
// /api/auth/login response carries this shape instead of AuthResponse. The
// frontend pivots to the 2FA challenge page; tokens are NOT yet issued.
interface TwoFactorChallengeResponse {
  requires2fa:             true;
  twoFactorChallengeToken: string;
}

type LoginResult = AuthResponse | TwoFactorChallengeResponse;

/** Type guard — narrows LoginResult to the 2FA branch. */
export function isTwoFactorChallenge(
  result: LoginResult,
): result is TwoFactorChallengeResponse {
  return (result as TwoFactorChallengeResponse).requires2fa === true;
}

const initialState: AuthState = {
  user: null,
  parentBranding: null,
  staff: null,
  token: localStorage.getItem('token'),
  refreshToken: localStorage.getItem('refreshToken'),
  isAuthenticated: !!localStorage.getItem('token'),
  isLoading: false,
  error: null,
};

// Async thunks
export const login = createAsyncThunk<LoginResult, LoginCredentials>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<LoginResult>>('/auth/login', credentials);

      if (response.data.success && response.data.data) {
        const data = response.data.data;

        // 2FA branch — do NOT store tokens; LoginPage handles navigation to
        // /login/2fa and persists the challenge token in sessionStorage.
        if (isTwoFactorChallenge(data)) {
          return data;
        }

        // Normal branch — store tokens
        localStorage.setItem('token',        data.tokens.accessToken);
        localStorage.setItem('refreshToken', data.tokens.refreshToken);
        clearImpersonationState();   // don't inherit a prior tab's impersonation
        return data;
      }

      return rejectWithValue(response.data.error?.message || 'Login failed');
    } catch (error) {
      const message = (error as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message || 'Login failed';
      return rejectWithValue(message);
    }
  },
);

/**
 * Wave 4.3 — POST /api/auth/2fa/verify-login.
 * Takes a challenge token + TOTP or recovery code, returns real auth tokens
 * on success. Used by TwoFactorChallengePage after the /login redirect.
 */
export const verifyTwoFactor = createAsyncThunk<
  AuthResponse,
  { challengeToken: string; token?: string; recoveryCode?: string; trustDevice?: boolean }
>(
  'auth/verifyTwoFactor',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<AuthResponse>>('/auth/2fa/verify-login', payload);
      if (response.data.success && response.data.data) {
        const { tokens } = response.data.data;
        localStorage.setItem('token',        tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
        clearImpersonationState();   // don't inherit a prior tab's impersonation
        return response.data.data;
      }
      return rejectWithValue(response.data.error?.message || 'Verification failed');
    } catch (error) {
      const message = (error as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message || 'Verification failed';
      return rejectWithValue(message);
    }
  },
);

export const signup = createAsyncThunk<AuthResponse, SignupData>(
  'auth/signup',
  async (signupData, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<AuthResponse>>('/auth/signup', signupData);
      
      if (response.data.success && response.data.data) {
        const { tokens } = response.data.data;
        
        // Store tokens in localStorage
        localStorage.setItem('token', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
        
        return response.data.data;
      }
      
      return rejectWithValue(response.data.error?.message || 'Signup failed');
    } catch (error) {
      const message = (error as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message || 'Signup failed';
      return rejectWithValue(message);
    }
  }
);

export const logout = createAsyncThunk<void, void>(
  'auth/logout',
  async () => {
    try {
      // B47 — send the refresh token too, so the server spends it. Sending only
      // the access token left the 30-day refresh token alive on the server
      // after every sign-out; the browser merely forgot it.
      await api.post('/auth/logout', { refreshToken: localStorage.getItem('refreshToken') ?? undefined });
    } catch (error) {
      // Even if the API call fails, we still want to clear local storage
      console.error('Logout API call failed:', error);
    } finally {
      // Always remove tokens from localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      // Clear any impersonation return-state so it can't leak into the next
      // login in the same tab (was persisting → isImpersonating() stayed true).
      clearImpersonationState();
    }
  }
);

export const refreshAccessToken = createAsyncThunk<{ token: string; refreshToken: string }, void>(
  'auth/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const currentRefreshToken = state.auth.refreshToken;
      
      if (!currentRefreshToken) {
        return rejectWithValue('No refresh token available');
      }
      
      const response = await api.post<ApiResponse<{ tokens: { accessToken: string; refreshToken: string } }>>(
        '/auth/refresh',
        { refreshToken: currentRefreshToken }
      );

      if (response.data.success && response.data.data) {
        // TokenPair uses `accessToken`, not `token` — reading `token` stored
        // undefined and logged the user out on the next request.
        const { accessToken: token, refreshToken } = response.data.data.tokens;
        
        // Update tokens in localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('refreshToken', refreshToken);
        
        return { token, refreshToken };
      }
      
      return rejectWithValue(response.data.error?.message || 'Token refresh failed');
    } catch (error) {
      const message = (error as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message || 'Token refresh failed';
      return rejectWithValue(message);
    }
  }
);

interface FetchCurrentUserResult {
  user: User;
  parentBranding: PublicBrandingData | null;
  staff: StaffContext | null;
}

/**
 * `quiet`: an expired session rejects instead of sending the page to /login —
 * for public pages that only want to know whether someone is signed in.
 */
export const fetchCurrentUser = createAsyncThunk<FetchCurrentUserResult, { quiet?: boolean } | void>(
  'auth/fetchCurrentUser',
  async (arg, { rejectWithValue }) => {
    try {
      const response = await api.get<ApiResponse<{ user: User; parentBranding: PublicBrandingData | null; staff: StaffContext | null }>>(
        '/auth/me',
        { skipAuthRedirect: !!(arg && arg.quiet) },
      );

      if (response.data.success && response.data.data) {
        return {
          user: response.data.data.user,
          parentBranding: response.data.data.parentBranding ?? null,
          staff: response.data.data.staff ?? null,
        };
      }

      return rejectWithValue({
        message: response.data.error?.message || 'Failed to fetch user',
        isAuthError: false
      });
    } catch (error) {
      const axiosError = error as { response?: { status?: number; data?: { error?: { message?: string } } } };
      const message = axiosError.response?.data?.error?.message || 'Failed to fetch user';
      const isAuthError = axiosError.response?.status === 401;

      return rejectWithValue({ message, isAuthError });
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCredentials: (state, action: PayloadAction<{ user: User; token: string; refreshToken: string | null }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        // Wave 4.3 — 2FA branch: don't authenticate yet, LoginPage navigates
        // the user to /login/2fa where they submit the TOTP code.
        if (isTwoFactorChallenge(action.payload)) {
          state.error = null;
          return;
        }
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.tokens.accessToken;
        state.refreshToken = action.payload.tokens.refreshToken;
        state.error = null;
      })
      .addCase(verifyTwoFactor.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyTwoFactor.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.tokens.accessToken;
        state.refreshToken = action.payload.tokens.refreshToken;
        state.error = null;
      })
      .addCase(verifyTwoFactor.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.error = action.payload as string;
      });

    // Signup
    builder
      .addCase(signup.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.tokens.accessToken;
        state.refreshToken = action.payload.tokens.refreshToken;
        state.error = null;
      })
      .addCase(signup.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.error = action.payload as string;
      });

    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.parentBranding = null;
        state.staff = null;
        state.token = null;
        state.refreshToken = null;
        state.error = null;
      })
      .addCase(logout.rejected, (state) => {
        // Even on error, clear the auth state
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.parentBranding = null;
        state.staff = null;
        state.token = null;
        state.refreshToken = null;
        state.error = null;
      });

    // Refresh token
    builder
      .addCase(refreshAccessToken.fulfilled, (state, action) => {
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.error = null;
      })
      .addCase(refreshAccessToken.rejected, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
      });

    // Fetch current user
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.parentBranding = action.payload.parentBranding;
        state.staff = action.payload.staff;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as { message: string; isAuthError: boolean } | undefined;
        
        // Only clear auth state if it's an authentication error (401)
        if (payload?.isAuthError) {
          state.isAuthenticated = false;
          state.user = null;
          state.token = null;
          state.refreshToken = null;
        }
        
        state.error = payload?.message || 'Failed to fetch user';
      });
  },
});

export const { clearError, setCredentials } = authSlice.actions;
export default authSlice.reducer;
